import { useRef, useState } from "react";

import { type CurrencyCode } from "@doitintl/cmp-models";
import DownloadIcon from "@mui/icons-material/SaveAlt";
import { Box, IconButton, Link, Stack, Tooltip, Typography } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import useMountEffect from "../../../../../../Components/hooks/useMountEffect";
import mixpanel from "../../../../../../utils/mixpanel";
import { type Explainer } from "../../../types";
import { CurrencySelector } from "../../Components/CurrencySelector";
import ExportToGoogleSheetsDialog from "../../ExportToGoogleSheetsDialog";
import { InvoiceExplainer } from "./Components/InvoiceExplainer";
import { TitleWithTooltip } from "./Components/TitleWithTooltip";

type Props = {
  explainer: Explainer;
  onCurrencyChange?: (currency: CurrencyCode) => void;
};

export const ExportOptions = [
  {
    value: "googleSheetsExport",
    label: "Export to Google Sheets",
  },
];

export function ExplainerSummaryPage({ explainer, onCurrencyChange }: Props) {
  const helpCenterLink = "https://help.doit.com/docs/billing/billing-explained/billing-differences";
  const [currency, setCurrency] = useState<CurrencyCode>("USD");
  const [exportGoogleSheetsDialogOpen, setExportGoogleSheetsDialogOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportIconRef = useRef<HTMLButtonElement | null>(null);

  const handleCurrencyChange = (currency: CurrencyCode) => {
    setCurrency(currency);
    if (onCurrencyChange) {
      onCurrencyChange(currency);
    }
  };

  const handleExportMenuOpen = () => {
    setExportMenuOpen(true);
  };
  const handleExportMenuClose = () => {
    setExportMenuOpen(false);
  };

  const handleExportOptionClick = (option: string) => {
    if (option === ExportOptions[0].value) {
      setExportGoogleSheetsDialogOpen(true);
    }
    setExportMenuOpen(false);
  };

  useMountEffect(() => {
    mixpanel.track("invoices.invoice.explainer.summary.open");
  });

  return (
    <Stack
      spacing={5}
      sx={{
        maxWidth: 800,
      }}
    >
      <Stack spacing={3}>
        <Typography variant="h3">Summary of your invoice</Typography>
        <Typography variant="body1">
          Here you can compare your usage and services costs with DoiT, to what DoiT is charged by AWS according to your
          usage.
        </Typography>
        <Typography variant="body1">
          There may be differences between your DoiT invoice and what DoiT is charged by AWS on your behalf. For more
          information, read about DoiT and AWS invoice differences in our{" "}
          <Link
            href={helpCenterLink}
            target="_blank"
            onClick={() => {
              mixpanel.track("invoices.invoice.explainer.helpcenter.open");
            }}
          >
            Help Center
          </Link>
          .
        </Typography>
      </Stack>
      <Stack spacing={2}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Box sx={{ width: "150px", display: "flex", alignItems: "center" }}>
            <Tooltip title="Export invoice explainer">
              <span>
                <IconButton aria-haspopup="true" ref={exportIconRef} onClick={handleExportMenuOpen}>
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Menu
              anchorEl={exportIconRef.current}
              open={exportMenuOpen}
              onClose={handleExportMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {ExportOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  onClick={() => {
                    handleExportOptionClick(option.value);
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Menu>
            <Box sx={{ ml: 1 }} />
            <CurrencySelector onCurrencyChange={handleCurrencyChange} />
          </Box>
        </Box>
        <TitleWithTooltip tooltip="What you see in AWS Cost Explorer" wordToUnderline="invoice">
          AWS' invoice to DoiT
        </TitleWithTooltip>
        <InvoiceExplainer type="aws" details={explainer.summary.aws} currency={currency} />
      </Stack>
      <Stack spacing={2}>
        <TitleWithTooltip tooltip="What you see in the DoiT Console" wordToUnderline="invoice">
          DoiT's invoice to you
        </TitleWithTooltip>
        <InvoiceExplainer type="doit" details={explainer.summary.doit} currency={currency} />
      </Stack>
      {exportGoogleSheetsDialogOpen && (
        <ExportToGoogleSheetsDialog
          isDialog={true}
          isDialogOpen={exportGoogleSheetsDialogOpen}
          onClose={() => {
            setExportGoogleSheetsDialogOpen(false);
          }}
          explainer={explainer}
          currency={currency}
        />
      )}
    </Stack>
  );
}
